import { StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import getStyles from '@front10/helpers/dist/UseStyles';
import { getUuidMin } from '@front10/helpers/dist/uuid';
import React, { Component, MutableRefObject, Ref } from 'react';
import Box from '../Box';
import Button from '../Button';
import Icon from '../Icon';
import Text from '../Text';
import { focus } from '../../../helpers/date';
import { isNilOrEmpty } from '../../../helpers/utils';

type NativeInputProps = JSX.IntrinsicElements['input'] & JSX.IntrinsicElements['textarea'];
type SelectedProps = Pick<
  NativeInputProps,
  | 'disabled'
  | 'inputMode'
  | 'name'
  | 'value'
  | 'placeholder'
  | 'onKeyUp'
  | 'onKeyDown'
  | 'onKeyPress'
  | 'onChange'
  | 'onFocus'
  | 'onPaste'
  | 'onBlur'
  | 'onClick'
  | 'spellCheck'
  | 'autoCorrect'
  | 'autoComplete'
  | 'maxLength'
  | 'minLength'
  | 'id'
>;

interface Props {
  type: 'text' | 'code' | 'email' | 'textarea';
  readonly: NativeInputProps['readOnly'];
  rounded: string; // TODO
  /**
   * Define left icons
   */
  leftIcons: string[];
  onLeftIconClick?: (icon: string, index: number) => void;
  leftIconsProps: $TSFixMe;
  leftButtonIconsProps: $TSFixMe;
  /**
   * Define right icons
   */
  rightIcons: string[];
  onRightIconClick?: (icon: string, index: number) => void;
  rightIconsProps: $TSFixMe;
  rightButtonIconsProps: $TSFixMe;
  rightBoxIconsProps: $TSFixMe;
  /**
   * Label of input
   */
  label: string;
  /**
   * rightLabel of the input
   */
  rightLabels: string[];
  /**
   * Props of rightLabels
   */
  rightLabelsProps: $TSFixMe;
  /**
   * Props of rightLabelsContainer
   */
  rightLabelsPropsContainer: $TSFixMe;
  /**
   * leftLabel of the input
   */
  leftLabels: string[];
  /**
   * Props of leftLabels
   */
  leftLabelsProps: $TSFixMe;
  /**
   * Props of leftLabelsPropsContainer
   */
  leftLabelsPropsContainer: $TSFixMe;
  /**
   * Description of input
   */
  description?: string;
  /**
   * Show label of input.
   */
  showLabel: boolean;
  /**
   * Show description of input
   */
  showDescription: boolean;
  /**
   *  TextLabel props for using into withStyle
   */
  textLabel: StyledProps<$TSFixMe>;
  /**
   * textDescription text description
   */
  textDescription: $TSFixMe;
  /**
   * ariaLabelledBy id
   */
  ariaLabelledBy?: NativeInputProps['aria-labelledby'];
  /**
   * aria-invalid
   */
  ariaInvalid?: NativeInputProps['aria-invalid'];
  /**
   *  Text of ariaLabel
   */
  ariaLabelDescription?: NativeInputProps['aria-describedby'];
  /**
   * Aria label invalid description
   */
  ariaLabelInvalidDescription?: string;
  /**
   * Show error on input
   */
  invalid: boolean;
  /**
   * Rounded right of the input
   */
  roundedRight: $TSFixMe;
  /**
   * Rounded left of the input
   */
  roundedLeft: $TSFixMe;
  /**
   * Array of left Tab Index
   */
  leftTabIndex: number[];
  /**
   * Array of right Tab Index
   */
  rightTabIndex: number[];
  /**
   * Array left aria label of buttons
   */
  leftAriaLabel: JSX.IntrinsicElements['button']['aria-label'][];
  /**
   * Array right aria label of buttons.
   */
  rightAriaLabel: JSX.IntrinsicElements['button']['aria-label'][];
  /**
   * Border of the input
   */
  border: boolean;
  /**
   * internal inputComponent Settings
   */
  inputSettings: $TSFixMe;
  /**
   * Define if label is inside
   */
  showLabelInside: boolean;
  /**
   * Aria autocomplete
   */
  ariaAutoComplete?: NativeInputProps['aria-autocomplete'];
  /**
   * aria-activedescendant
   */
  ariaActivedescendant?: NativeInputProps['aria-activedescendant'];
  /**
   * Focus references
   */
  focusReferences?: Ref<$TSFixMe>;
  /**
   * Data test ID of Input
   */
  dataTestId: string;
  /**
   * Show focus visual cue
   */
  showFocusVisualCue: boolean;
  /**
   * font-size of the input.
   */
  fontSizeInput: $TSFixMe;
  /**
   * Class to apply when component is disabled
   */
  disabledClass?: string;
  /**
   * Class when component receive focus
   */
  focusClass?: string;
  /**
   * Class to apply when component is invalid
   */
  invalidClass: string;
  rootProps?: JSX.IntrinsicElements['div'];
  /**
   * Class to apply when the component has a value
   */
  onValueClass: string;
  makeAccessibleByArrowsKeys?: boolean;
  makeAccessibleByArrowsKeysRole?: string;
  comboboxSettings?: any;
  showLabelHidden?: boolean;
  onRightIconBlur?: () => void;
  rightIconDataTestId?: string;
  buttonRightIconId?: string;
}

export type InputProps = StyledProps<Props & SelectedProps>;

class Input extends Component<InputProps, { focus: boolean }> {
  inputRef: MutableRefObject<unknown> = React.createRef();

  generatedId?: string;

  keyCode = {
    enter: 13
  };

  state = {
    focus: false
  };

  onFocus = (event: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
    const { onFocus } = this.props;
    this.setState({ focus: true });
    if (onFocus) {
      onFocus(event);
    }
  };

  onBlur = (event: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
    const { onBlur } = this.props;
    this.setState({ focus: false });
    if (onBlur) {
      onBlur(event);
    }
  };

  onKeyPressIcon = (
    event: React.KeyboardEvent,
    position: 'left' | 'right',
    icon: string,
    index: number
  ) => {
    if (event.keyCode === this.keyCode.enter) {
      const { onLeftIconClick, onRightIconClick } = this.props;
      if (position === 'left' && onLeftIconClick) {
        focus(this.inputRef);
        onLeftIconClick(icon, index);
      } else if (position === 'right' && onRightIconClick) {
        focus(this.inputRef);
        onRightIconClick(icon, index);
      }
    }
  };

  leftIconClick = (icon: string, index: number) => () => {
    const { disabled, onLeftIconClick } = this.props;
    if (!disabled) {
      focus(this.inputRef);
      if (typeof onLeftIconClick === 'function') {
        onLeftIconClick(icon, index);
      }
    }
  };

  rightIconClick = (icon: string, index: number) => () => {
    const { disabled, onRightIconClick } = this.props;
    if (!disabled) {
      focus(this.inputRef);
      if (typeof onRightIconClick === 'function') {
        onRightIconClick(icon, index);
      }
    }
  };

  handleRef = (element: HTMLInputElement & HTMLTextAreaElement) => {
    const { focusReferences } = this.props;
    this.inputRef.current = element;

    if (typeof focusReferences === 'function') {
      focusReferences(element);
    } else {
      // @ts-expect-error
      focusReferences.current = element;
    }
  };
  focus = () => {
    focus(this.inputRef);
  };
  render() {
    const {
      type,
      dataTestId,
      name,
      onClick,
      placeholder,
      onKeyUp,
      onKeyDown,
      onKeyPress,
      leftIcons,
      leftIconsProps,
      leftButtonIconsProps,
      rightIcons,
      rightIconsProps,
      rightButtonIconsProps,
      rightBoxIconsProps,
      disabled,
      readonly,
      label,
      description,
      showLabel,
      showDescription,
      ariaLabelledBy,
      ariaLabelDescription,
      textLabel,
      textDescription,
      ariaInvalid,
      ariaLabelInvalidDescription,
      ariaAutoComplete,
      invalid,
      rounded,
      roundedRight,
      roundedLeft,
      leftTabIndex: leftTabIndices,
      leftAriaLabel,
      rightAriaLabel,
      showLabelInside,
      autoComplete,
      autoCorrect,
      ariaActivedescendant,
      rightLabels,
      rightLabelsProps,
      rightLabelsPropsContainer,
      leftLabels,
      leftLabelsProps,
      leftLabelsPropsContainer,
      inputMode,
      maxLength,
      minLength,
      onChange,
      value,
      id: receivedId,
      showFocusVisualCue,
      spellCheck,
      inputSettings,
      disabledClass,
      focusClass,
      invalidClass,
      rootProps,
      onValueClass,
      makeAccessibleByArrowsKeysRole,
      makeAccessibleByArrowsKeys,
      comboboxSettings,
      showLabelHidden,
      rightIconDataTestId,
      buttonRightIconId
    } = this.props;
    const { focus } = this.state;
    const { direction, theme } = this.context;
    const [className, style] = getStyles(this.props, { direction, theme });
    const [inputClassName, inputStyle] = getStyles(inputSettings, { direction, theme });

    let inputClass = inputClassName;
    let componentClass = `${className}`;
    let labelClass = textLabel.className || '';
    if (disabled) {
      labelClass += ` ${textLabel.disabledClass || 'fr-input-label-disabled'}`;
      componentClass += ` ${disabledClass || 'fr-input-wrapper-disabled'}`;
      inputClass += ` ${inputSettings.disabledClass || 'fr-input-disabled'}`;
    }
    if (showLabelHidden) {
      labelClass = 'fr-sr-only';
    }

    if (!receivedId) {
      this.generatedId = this.generatedId || getUuidMin();
    }
    const id = receivedId || this.generatedId;
    const idAriaLabelDescription = id;
    const inputId = `input-${id}`;
    const [rootClass] = getStyles({ bgColor: 'white' }, { direction, theme });

    const props = {
      id: inputId,
      name,
      'data-testid': dataTestId,
      placeholder,
      onKeyUp,
      onKeyDown,
      onKeyPress,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onPaste: this.props.onPaste,
      onClick,
      onChange,
      value,
      disabled,
      maxLength,
      minLength,
      'aria-labelledby': showLabel ? ariaLabelledBy : undefined,
      'aria-invalid': ariaInvalid,
      'aria-label': !showLabel ? label : undefined,
      'aria-describedby': ariaLabelDescription && idAriaLabelDescription,
      'aria-activedescendant': ariaActivedescendant,
      'aria-autocomplete': ariaAutoComplete,
      autoCorrect,
      spellCheck,
      readOnly: readonly,
      className: `fr-form-control fr-text-truncate ${inputClass}`,
      style: inputStyle,
      autoComplete,
      inputMode
    };

    let currentOnValueClass = '';
    if (!isNilOrEmpty(onValueClass) && !isNilOrEmpty(value)) {
      currentOnValueClass = onValueClass;
    }

    return (
      <>
        {showLabel && (
          <Text
            display="inline-block"
            textAlign="left"
            width="100"
            type="label"
            {...textLabel}
            id={
              rootProps
                ? `label-${rootProps?.['aria-labelledby']}`
                : `label-${props['name']}-${props['id']}`
            }
            className={labelClass}
            htmlFor={inputId}
            disabled={disabled}
          >
            {label}
          </Text>
        )}
        <div className={`${componentClass} ${rootClass}`} style={style}>
          <div
            className={`fr-input fr-input-group fr-w-100 ${
              focus && showFocusVisualCue && !invalid ? `${focusClass || 'fr-focus:focus'}` : ''
            } ${invalid && showFocusVisualCue ? invalidClass : ''} ${currentOnValueClass}`}
            role={
              makeAccessibleByArrowsKeys
                ? makeAccessibleByArrowsKeysRole
                  ? makeAccessibleByArrowsKeysRole
                  : undefined
                : undefined
            }
            {...comboboxSettings}
          >
            {leftLabels.map((leftLabel) => (
              <Box key={label} {...leftLabelsPropsContainer} className="fr-input-group-prepend">
                <Text type="label" {...leftLabelsProps}>
                  {leftLabel}
                </Text>
              </Box>
            ))}
            {leftIcons.map((icon, index) => {
              const leftTabIndex = leftTabIndices[index];
              const interactive = !(leftTabIndex < 0);
              const LeftIconContainer = interactive ? Button : Box;
              const defaultStyles = interactive
                ? undefined
                : {
                    display: 'inline-flex',
                    paddingLeft: '2',
                    alignSelf: 'center'
                  };
              return (
                <div aria-hidden="true" key={icon} className="fr-input-group-prepend">
                  <LeftIconContainer
                    dataTestId="buttonLefticons"
                    {...defaultStyles}
                    {...leftButtonIconsProps}
                    onClick={interactive ? this.leftIconClick(icon, index) : undefined}
                    onKeyDown={
                      interactive
                        ? (event: $TSFixMe) => {
                            this.onKeyPressIcon(event, 'left', icon, index);
                          }
                        : undefined
                    }
                    roundedLeft={index === 0 ? roundedLeft || rounded : ''}
                    tabIndex={interactive ? leftTabIndex : undefined}
                    ariaLabel={interactive ? leftAriaLabel[index] : undefined}
                    paddingRight="0"
                  >
                    <Icon align="middle" icon={icon} {...leftIconsProps} />
                  </LeftIconContainer>
                </div>
              );
            })}
            {showLabelInside && (
              <div aria-hidden="true" className="fr-input-group-prepend">
                <Text ariaHidden="false" type="label" {...textLabel} htmlFor={inputId}>
                  {label}
                </Text>
              </div>
            )}
            {type === 'textarea' && <textarea {...props} ref={this.handleRef} />}
            {type !== 'textarea' && <input type={type} {...props} ref={this.handleRef} />}
            {rightLabels.map((rightLabel) => (
              <Box key={label} {...rightLabelsPropsContainer} className="fr-input-group-append">
                <Text {...rightLabelsProps}>{rightLabel}</Text>
              </Box>
            ))}
            {rightIcons.map((icon, index) => {
              const rightTabIndex = this.props.rightTabIndex[index];
              const interactive = !(rightTabIndex < 0);
              const RightIconContainer = interactive ? Button : Box;
              const iconStyle = interactive ? rightButtonIconsProps : rightBoxIconsProps;
              return (
                <Box key={icon} className="fr-input-group-append">
                  <RightIconContainer
                    dataTestId={rightIconDataTestId ?? 'buttonRightIcons'}
                    color="transparent"
                    {...iconStyle}
                    border={false}
                    onBlur={(e) => {
                      if (this.props?.onRightIconBlur) {
                        this.props?.onRightIconBlur();
                      }
                    }}
                    onClick={this.rightIconClick(icon, index)}
                    onKeyDown={(event: any) =>
                      !disabled ? this.onKeyPressIcon(event, 'right', icon, index) : ''
                    }
                    roundedRight={index === rightIcons.length - 1 ? roundedRight || rounded : ''}
                    ariaLabel={interactive ? rightAriaLabel[index] : null}
                    ariaHidden={!interactive ? true : null}
                    id={buttonRightIconId ? `${buttonRightIconId}` : undefined}
                  >
                    <Icon align="middle" icon={icon} {...rightIconsProps} />
                  </RightIconContainer>
                </Box>
              );
            })}
          </div>
        </div>
        {showDescription && description && (
          <small
            className={`${
              invalid ? `${textDescription.invalidClass || 'fr-input-description-invalid'}` : ''
            }`}
            {...textDescription}
          >
            {description}
          </small>
        )}
        {ariaInvalid && (
          <p id={idAriaLabelDescription} className="fr-sr-only" aria-hidden="true">
            {ariaLabelInvalidDescription}
          </p>
        )}
        {ariaLabelDescription && !ariaInvalid && (
          <p id={idAriaLabelDescription} className="fr-sr-only" aria-hidden="true">
            {ariaLabelDescription}
          </p>
        )}
      </>
    );
  }

  static defaultProps = {
    focusReferences: React.createRef(),
    onLeftIconClick: () => {},
    onRightIconClick: () => {},
    border: true,
    dataTestId: 'Input',
    disabled: false,
    type: 'text',
    value: '',
    readonly: false,
    rounded: '0',
    leftIcons: [],
    rightIcons: [],
    leftLabels: [],
    leftLabelsProps: {},
    leftLabelsPropsContainer: {},
    rightLabels: [],
    rightLabelsProps: {},
    rightLabelsPropsContainer: {},
    showLabel: false,
    showDescription: false,
    textLabel: {
      fontWeight: 'bold',
      display: 'flex',
      alignSelf: 'center',
      margin: '0',
      paddingLeft: '2'
    },
    textDescription: {},
    invalid: false,
    roundedRight: '',
    roundedLeft: '',
    leftTabIndex: [],
    rightTabIndex: [],
    leftAriaLabel: [],
    rightAriaLabel: [],
    leftIconsProps: {
      size: 16
    },
    leftButtonIconsProps: {
      border: false,
      color: 'transparent'
    },
    rightIconsProps: {
      size: 16
    },
    rightButtonIconsProps: null,
    rightBoxIconsProps: null,
    inputSettings: {},
    showLabelInside: false,
    showFocusVisualCue: true,
    inputMode: undefined,
    fontSizeInput: '',
    invalidClass: 'fr-input-invalid',
    onValueClass: 'fr-value-selected',
    showLabelHidden: false
  };

  static componentName = 'Input';
  static contextType = Context;
}

export default Input;
